/**
 * Zod schema prop introspection.
 *
 * Given a `ComponentRegistration`, resolves the prop names that make up
 * the component's surface area. The config page uses this list to render
 * editable labels ("pills") and to tell admins which prop names they may
 * reference from a rule.
 *
 * The registry stores the schema as a bare `ZodType<P>`, which is the
 * widest Zod type — not every schema is a `ZodObject`. When the schema
 * is an object we use its `.shape` to enumerate keys; otherwise we fall
 * back to the keys of `defaultPropLabels` from the registration, which
 * the module author is required to fill in whenever the schema is not
 * introspectable.
 *
 * Keeping this helper in its own module (rather than inline in the page)
 * makes the fallback path testable without a full render.
 */

import { z, type ZodType } from 'zod';

import type { ComponentRegistration } from '@components/shared/configurable';

/**
 * Shape narrowing type. Zod's public `ZodType` does not expose `.shape`,
 * so we duck-type the check with a minimal interface. The cast happens
 * at the boundary so callers stay untyped-safe.
 */
interface ZodObjectLike {
  shape: Record<string, ZodType>;
}

/**
 * Heuristic for "this looks like a ZodObject". Avoids `instanceof`
 * because Zod v4 can return wrapped schemas (branded / pipes) that still
 * expose a `.shape` but aren't strictly instances of `ZodObject`.
 */
function isZodObjectLike(schema: unknown): schema is ZodObjectLike {
  return (
    schema !== null &&
    typeof schema === 'object' &&
    'shape' in schema &&
    typeof (schema as { shape: unknown }).shape === 'object' &&
    (schema as { shape: unknown }).shape !== null
  );
}

/**
 * Return the list of prop names declared for a component.
 *
 * Resolution order:
 * 1. If the registration's `propsSchema` is a `ZodObject`, return the
 *    top-level keys from `.shape` in insertion order.
 * 2. Otherwise, return the keys of `defaultPropLabels` — this is the
 *    only other authoritative source and matches the spec's guidance
 *    for schemas that aren't plain objects.
 */
export function getPropNamesFromRegistration(
  registration: ComponentRegistration<unknown>,
): string[] {
  const schema = registration.propsSchema as unknown;

  if (isZodObjectLike(schema)) {
    return Object.keys(schema.shape);
  }

  return Object.keys(registration.defaultPropLabels);
}

/**
 * Return per-prop JSON Schema fragments for a component registration.
 *
 * Resolution:
 * 1. Run `z.toJSONSchema(registration.propsSchema)` inside try/catch so a
 *    schema Zod cannot serialise (custom types, infinite recursion, etc.)
 *    never bubbles up and crashes the admin page.
 * 2. Verify the result is an object-typed JSON Schema (`type === 'object'`)
 *    with a plain `properties` object. Anything else — non-object schemas,
 *    missing `properties`, exotic shapes — yields `undefined`.
 * 3. Treat zero-prop components (`properties` is empty) as "no schema to
 *    send" and return `undefined` so the caller can omit the field rather
 *    than emit an empty `{}`.
 *
 * The returned map is keyed by prop name with each value the JSON Schema
 * fragment for that prop. The translator uses this to type-check
 * `setProp` / `filterPropValues` / `requireProp` effects.
 */
export function getPropJsonSchemasFromRegistration(
  registration: ComponentRegistration<unknown>,
): Record<string, unknown> | undefined {
  let jsonSchema: unknown;
  try {
    jsonSchema = z.toJSONSchema(registration.propsSchema);
  } catch {
    return undefined;
  }

  if (jsonSchema === null || typeof jsonSchema !== 'object') {
    return undefined;
  }

  const asRecord = jsonSchema as Record<string, unknown>;
  if (asRecord.type !== 'object') {
    return undefined;
  }

  const properties = asRecord.properties;
  if (
    properties === null ||
    typeof properties !== 'object' ||
    Array.isArray(properties)
  ) {
    return undefined;
  }

  const propertiesRecord = properties as Record<string, unknown>;
  if (Object.keys(propertiesRecord).length === 0) {
    return undefined;
  }

  return propertiesRecord;
}
